import moment from "moment";
import { get } from "lodash";
import { getAppFeatureProp } from "./app-feature-util";

export const generateYearOptions = (thisYear = new Date().getFullYear()) => {
  let arr = [];
  for (let i = 2021; i <= thisYear; i++)
    arr.push({
      value: i + "",
      selected: i == thisYear ? true : false,
    });
  return arr;
};

export const generateStepper = (length) => {
  let arr = [];
  for (let i = 0; i < length; i++) arr.push({ active: false, value: `${i + 1}` });
  return arr;
};

export const generateStepperData = (stepper, currentStep) => {
  return stepper.map((step, index) => ({
    ...step,
    active: index === currentStep - 1,
    previous: index < currentStep - 1,
  }));
};

export const getVendorFeatureProp = (configMixinComponent) => {
  const businessAccountCMS =
    getAppFeatureProp(configMixinComponent.platformConfig.cmsGeneralFeatureList, "business_account") || {};
  const vendors = getAppFeatureProp(businessAccountCMS.props, "vendors");
  const currentVendorName = get(configMixinComponent, "currencyAccounts.0.vendor.name");
  return vendors.props.find((vendor) => vendor.name === currentVendorName);
};

export const isInternationalTransferEnabled = (component) => {
  return get(getVendorFeatureProp(component), "value.transfer.international_transfer_enabled");
};

export const getRecipientTypes = (component) => {
  return get(getVendorFeatureProp(component), "value.recipient.recipient_types", []);
};

export const isLocalTransferWithForeignCurrencyEnabled = (component, companyId) => {
  const cmsConfig = getAppFeatureProp(
    component.platformConfig.cmsGeneralFeatureList,
    "sba_local_transfer_with_foreign_currency"
  );
  const isFeatureEnabled = cmsConfig.enabled;
  const isWhitelistCompanyOnly = get(cmsConfig, "value.whitelist_company_only", false);
  const whitelistCompanies = get(cmsConfig, "value.whitelist_company_ids", []);
  return isFeatureEnabled && (!isWhitelistCompanyOnly || whitelistCompanies.includes(companyId));
};

export const isTransferV3 = (component, companyId) => {
  const cmsConfig = getAppFeatureProp(component.platformConfig.cmsGeneralFeatureList, "sba_transfer_flow_v3");
  const isFeatureEnabled = cmsConfig.enabled;
  const isWhitelistCompanyOnly = get(cmsConfig, "value.whitelist_company_only", false);
  const whitelistCompanies = get(cmsConfig, "value.whitelist_company_ids", []);
  return isFeatureEnabled && (!isWhitelistCompanyOnly || whitelistCompanies.includes(companyId));
};

export const getOtpActions = (component) => {
  const profile = getAppFeatureProp(component.platformConfig.cmsAppFeatures, "profile");
  const two_factor_authentication = getAppFeatureProp(profile.props, "two_factor_authentication");
  return getAppFeatureProp(two_factor_authentication.props, "2fa_actions");
};

export const getVendorFeaturePropByCurrencyAccount = (configMixinComponent, currencyAccount) => {
  const businessAccountCMS =
    getAppFeatureProp(configMixinComponent.platformConfig.cmsGeneralFeatureList, "business_account") || {};
  const vendors = getAppFeatureProp(businessAccountCMS.props, "vendors");
  const vendorName = get(currencyAccount, "vendor.name") || get(configMixinComponent, "currencyAccounts.0.vendor.name");
  return vendors.props.find((vendor) => vendor.name === vendorName);
};

export const getSBAVendorFeatureProp = (platformConfig, vendorName) => {
  const businessAccountCMS = getAppFeatureProp(platformConfig.cmsGeneralFeatureList, "business_account") || {};
  const vendors = getAppFeatureProp(businessAccountCMS.props, "vendors");
  return vendors.props.find((vendor) => vendor.name === vendorName);
};

export const isAbleToShowSbaLandingPage = (configMixinComponent) => {
  const businessAccountCMS =
    getAppFeatureProp(configMixinComponent.platformConfig.cmsGeneralFeatureList, "business_account") || {};
  const sbaOnboarding = getAppFeatureProp(businessAccountCMS.props, "sba_onboarding") || {};
  return !!sbaOnboarding.enabled && get(sbaOnboarding, "value.show_landing_page", false);
};

export const isAbleToShowSbaQuestionnairePage = (configMixinComponent) => {
  const businessAccountCMS =
    getAppFeatureProp(configMixinComponent.platformConfig.cmsGeneralFeatureList, "business_account") || {};
  const sbaOnboarding = getAppFeatureProp(businessAccountCMS.props, "sba_onboarding") || {};
  return !!sbaOnboarding.enabled && get(sbaOnboarding, "value.show_questionnaire_page", false);
};

/**
 * @enum CardDialogTypes
 * @description To know which dialog will be opened
 */
export const CardDialogTypes = {
  INFO: "INFO", // green icon
  WARING: "WARING", // yellow icon
  ERROR: "ERROR", // red icon
};

/**
 * @enum DialogNames
 * @description To know which dialog will be opened
 */
export const DialogNames = {
  ISSUE_CARD: "ISSUE_CARD",
  TERMINATE_CARD: "TERMINATE_CARD",
  CARD_REQUEST_FAILED: "CARD_REQUEST_FAILED",
};

/**
 * @enum CardActionStatuses
 * @description To know what action user can perform: issue card, terminate card,...
 */
export const CardActionStatuses = {
  DISABLED: "DISABLED", // cannot perform action
  INPROGRESS: "INPROGRESS", // the request has been sent
  AVAILABLE: "AVAILABLE", // user can do the action
};

export const TransactionDirection = {
  DEBIT: "debit",
  CREDIT: "credit",
};

export const SBAUserPermission = {
  FULL: "full",
  VIEW_ONLY: "view-only",
};

export const TransferType = {
  LOCAL: "local",
  TOP_UP: "top_up",
  INTERNATIONAL: "international",
  CONVERSION: "conversion",
};

export const AppcuesEventName = {
  FLOW_COMPLETED: "flow_completed",
  STEP_STARTED: "step_started",
  STEP_ATTEMPTED: "step_attempted",
  STEP_CHILD_ACTIVATED: "step_child_activated",
  STEP_INTERACTED: "step_interacted",
  STEP_CHILD_DEACTIVATED: "step_child_deactivated",
  STEP_COMPLETED: "step_completed",
};

export const TransactionCategory = {
  ACCOUNT: "account",
  CARD: "virtual_card",
};

export const isShowAnnouncement = (data) => {
  if (!data) return false;
  if (!data.enabled) return false;
  if (!data.start_time || !data.end_time) return true;

  const convertedStartDate = moment.utc(data.start_time);
  const convertedEndDate = moment.utc(data.end_time);
  if (!convertedStartDate.isValid() || !convertedEndDate.isValid()) return false;
  return moment.utc().isBetween(convertedStartDate, convertedEndDate);
};

export const ProcessStepContent = {
  "application-received": "Application Received",
  "identify-verification": "Identify Verification",
  "pending-incorporation": "Pending Incorporation",
  "processing-application": "Account Opening In Progress",
};

export const ProcessStepNotification = {
  "identify-verification": "We are verifying your identity",
  "pending-incorporation": "We are incorporating your company",
  "processing-application": "Your account opening is in progress",
};

export const cleanObject = (obj) => {
  // eslint-disable-next-line no-unused-vars
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v === false || !!v));
};

// copy from sba-common-library STATUS_CODES
export const STATUS_CODES = {
  SUCCESS: "200200",
};
